/**
 * Discount Types & Interfaces
 * Matching backend DTOs and database schema
 */

export enum DiscountType {
  PERCENTAGE = 'PERCENTAGE',
  FIXED_AMOUNT = 'FIXED_AMOUNT',
  BUY_X_GET_Y = 'BUY_X_GET_Y',
  FREE_SHIPPING = 'FREE_SHIPPING',
}

export enum DiscountScope {
  STORE_WIDE = 'STORE_WIDE',
  SPECIFIC_PRODUCTS = 'SPECIFIC_PRODUCTS',
  MINIMUM_PURCHASE = 'MINIMUM_PURCHASE',
  CATEGORIES = 'CATEGORIES',
}

export interface Discount {
  id: string;
  storeId: string;
  code: string | null;
  name: string;
  description: string | null;
  type: DiscountType;
  value: number;
  scope: DiscountScope;
  minimumPurchase: number | null;
  maximumDiscount: number | null;
  buyQuantity: number | null;
  getQuantity: number | null;
  usageLimit: number | null;
  usageCount: number;
  perCustomerLimit: number | null;
  startDate: string;
  endDate: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  products?: DiscountProduct[];
  usages?: DiscountUsage[];
}

export interface DiscountProduct {
  id: string;
  discountId: string;
  productId: string;
  role: 'BUY' | 'GET';
  createdAt: string;
  product?: {
    id: string;
    name: string;
    sku: string | null;
  };
}

export interface DiscountUsage {
  id: string;
  discountId: string;
  customerId: string | null;
  orderId: string | null;
  discountAmount: number;
  createdAt: string;
}

export interface CreateDiscountDto {
  code?: string | null;
  name: string;
  description?: string | null;
  type: DiscountType;
  value: number;
  scope: DiscountScope;
  minimumPurchase?: number | null;
  maximumDiscount?: number | null;
  usageLimit?: number | null;
  perCustomerLimit?: number | null;
  startDate: string;
  endDate?: string | null;
  active?: boolean;
  productIds?: string[];
  categoryIds?: string[];
  // Buy X Get Y specific fields
  buyQuantity?: number | null;
  getQuantity?: number | null;
  buyProductIds?: string[];
  getProductIds?: string[];
  buyCategoryIds?: string[];
  getCategoryIds?: string[];
}

export interface UpdateDiscountDto {
  code?: string | null;
  name?: string;
  description?: string | null;
  type?: DiscountType;
  value?: number;
  scope?: DiscountScope;
  minimumPurchase?: number | null;
  maximumDiscount?: number | null;
  usageLimit?: number | null;
  perCustomerLimit?: number | null;
  startDate?: string;
  endDate?: string | null;
  active?: boolean;
  productIds?: string[];
  categoryIds?: string[];
  // Buy X Get Y specific fields
  buyQuantity?: number | null;
  getQuantity?: number | null;
  buyProductIds?: string[];
  getProductIds?: string[];
  buyCategoryIds?: string[];
  getCategoryIds?: string[];
}

export interface DiscountAnalytics {
  totalUsage: number;
  totalRevenue: number;
  averageDiscount: number;
  uniqueCustomers: number;
}

export interface DiscountsResponse {
  data: Discount[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface DiscountFilters {
  status?: 'active' | 'inactive' | 'expired' | 'all';
  type?: DiscountType;
  scope?: DiscountScope;
  search?: string;
  page?: number;
  limit?: number;
}

// Helper type guards
export const isPercentageDiscount = (discount: Discount): boolean => {
  return discount.type === DiscountType.PERCENTAGE;
};

export const isFixedAmountDiscount = (discount: Discount): boolean => {
  return discount.type === DiscountType.FIXED_AMOUNT;
};

export const isActiveDiscount = (discount: Discount): boolean => {
  const now = new Date();
  const startDate = new Date(discount.startDate);
  const endDate = discount.endDate ? new Date(discount.endDate) : null;

  return (
    discount.active &&
    startDate <= now &&
    (!endDate || endDate >= now) &&
    (!discount.usageLimit || discount.usageCount < discount.usageLimit)
  );
};

export const getDiscountStatus = (discount: Discount): 'active' | 'inactive' | 'expired' | 'upcoming' => {
  const now = new Date();
  const startDate = new Date(discount.startDate);
  const endDate = discount.endDate ? new Date(discount.endDate) : null;

  if (!discount.active) return 'inactive';
  if (startDate > now) return 'upcoming';
  if (endDate && endDate < now) return 'expired';
  if (discount.usageLimit && discount.usageCount >= discount.usageLimit) return 'expired';
  return 'active';
};

// Format helpers
export const formatDiscountValue = (discount: Discount): string => {
  switch (discount.type) {
    case DiscountType.PERCENTAGE:
      return `${discount.value}% off`;
    case DiscountType.FIXED_AMOUNT:
      return `$${discount.value} off`;
    case DiscountType.FREE_SHIPPING:
      return 'Free shipping';
    case DiscountType.BUY_X_GET_Y:
      return `Buy ${discount.value} get discount`;
    default:
      return `${discount.value}`;
  }
};

export const formatDiscountScope = (scope: DiscountScope): string => {
  switch (scope) {
    case DiscountScope.STORE_WIDE:
      return 'Store-wide';
    case DiscountScope.SPECIFIC_PRODUCTS:
      return 'Specific products';
    case DiscountScope.MINIMUM_PURCHASE:
      return 'Minimum purchase';
    case DiscountScope.CATEGORIES:
      return 'Categories';
    default:
      return scope;
  }
};

export const getDiscountTypeLabel = (type: DiscountType): string => {
  switch (type) {
    case DiscountType.PERCENTAGE:
      return 'Percentage';
    case DiscountType.FIXED_AMOUNT:
      return 'Fixed Amount';
    case DiscountType.FREE_SHIPPING:
      return 'Free Shipping';
    case DiscountType.BUY_X_GET_Y:
      return 'Buy X Get Y';
    default:
      return type;
  }
};

export const getDiscountScopeLabel = (scope: DiscountScope): string => {
  return formatDiscountScope(scope);
};

export const getStatusColor = (status: 'active' | 'inactive' | 'expired' | 'upcoming'): string => {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'inactive':
      return 'bg-gray-100 text-gray-800';
    case 'expired':
      return 'bg-red-100 text-red-800';
    case 'upcoming':
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};
